import _ from "lodash";
import type { CashSettingsController } from "../../../api/src/cash-settings/cash-settings.controller";
import type { InsightsController } from "../../../api/src/insights/insights.controller";
import type { AccountWithMetrics } from "../../../api/src/accounts/accounts.service";
import type { HouseholdWithMetrics } from "../../../api/src/households/households.service";
import type { UnpackResponse } from "../../../api/src/lib";
import { useQueryAccountCashSettings } from "../cash/lib";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import { useAuthenticatedFetch, useAuthenticatedQuery } from "../lib/api";

export type InsightEntity = "account" | "household";

export type InsightKey =
  | "accounts-missing-cash-target"
  | "accounts-excess-cash"
  | "accounts-cash-activity"
  | "accounts-new"
  | "accounts-no-fee-structure"
  | "accounts-no-model"
  | "households-no-fee-structure"
  | "households-no-model";

export const INSIGHT_ENTITY: Record<InsightKey, InsightEntity> = {
  "accounts-missing-cash-target": "account",
  "accounts-excess-cash": "account",
  "accounts-cash-activity": "account",
  "accounts-new": "account",
  "accounts-no-fee-structure": "account",
  "accounts-no-model": "account",
  "households-no-fee-structure": "household",
  "households-no-model": "household",
};

type CashSetting = UnpackResponse<
  CashSettingsController["getAllAccountSettings"]
>["data"][number];

type AccountForRule = Pick<
  AccountWithMetrics,
  "id" | "householdId" | "feeStructureId" | "assignedModelId" | "accountBalance"
>;

type HouseholdForRule = Pick<
  HouseholdWithMetrics,
  "id" | "feeStructureId" | "assignedModelId" | "householdBalance"
>;

/** An account has no cash target when every cash-target field is null. */
export function noCashTarget(account: CashSetting) {
  return (
    account.cashTargetAbsolute === null &&
    account.cashTargetPercent === null &&
    account.cashTargetMinAbsolute === null &&
    account.cashTargetMinPercent === null &&
    account.cashTargetMaxAbsolute === null &&
    account.cashTargetMaxPercent === null
  );
}

export function accountsWithoutFeeStructure(
  accounts: AccountForRule[],
  householdsById: Record<number, Pick<HouseholdForRule, "feeStructureId">>,
) {
  return accounts.filter(
    (account) =>
      account.feeStructureId == null &&
      (account.accountBalance ?? 0) > 0 &&
      (account.householdId == null ||
        typeof householdsById[account.householdId] === "undefined" ||
        householdsById[account.householdId].feeStructureId == null),
  );
}

export function accountsWithoutModel(
  accounts: AccountForRule[],
  householdsById: Record<number, Pick<HouseholdForRule, "assignedModelId">>,
) {
  return accounts.filter(
    (account) =>
      account.assignedModelId == null &&
      (account.householdId == null ||
        typeof householdsById[account.householdId] === "undefined" ||
        householdsById[account.householdId].assignedModelId == null) &&
      (account.accountBalance ?? 0) > 0,
  );
}

export function householdsWithoutFeeStructure(households: HouseholdForRule[]) {
  return households.filter(
    (household) =>
      household.feeStructureId == null && (household.householdBalance ?? 0) > 0,
  );
}

export function householdsWithoutModel(households: HouseholdForRule[]) {
  return households.filter(
    (household) =>
      household.assignedModelId == null &&
      (household.householdBalance ?? 0) > 0,
  );
}

/**
 * Re-derive the account ids backing an account-entity insight key by calling
 * the same endpoint(s) the home-page tile uses. All hooks are mounted
 * unconditionally and gated with `enabled` to respect the rules of hooks.
 */
export function useInsightAccountIds(key: InsightKey | null): {
  isPending: boolean;
  ids: number[] | undefined;
} {
  const wantCashSettings = key === "accounts-missing-cash-target";
  const wantOpportunities = key === "accounts-excess-cash";
  const wantCashActivity = key === "accounts-cash-activity";
  const wantNewAccounts = key === "accounts-new";
  const wantAccountsAndHouseholds =
    key === "accounts-no-fee-structure" || key === "accounts-no-model";

  // Every page of `GET /v1/cash-settings/accounts?requireBalance=true`
  // (APP-1651), as `UnassignedCashSettings.tsx` reads it.
  const cashSettings = useQueryAccountCashSettings({
    requireBalance: true,
    enabled: wantCashSettings,
  });

  const opportunities = useAuthenticatedFetch<
    UnpackResponse<InsightsController["accountOpportunities"]>
  >("/insights/account-opportunities", {}, { enabled: wantOpportunities });

  const cashActivity = useAuthenticatedFetch<
    UnpackResponse<InsightsController["cashActivity"]>
  >("/insights/cash-activity", {}, { enabled: wantCashActivity });

  const newAccounts = useAuthenticatedQuery<
    UnpackResponse<InsightsController["newAccounts"]>,
    number[],
    [string, string]
  >({
    queryKey: ["insights", "new-accounts"],
    queryFn: async (data) => data.data,
    queryOptions: { enabled: wantNewAccounts },
  });

  const accounts = useQueryAllAccounts({
    includeBalances: true,
    enabled: wantAccountsAndHouseholds,
  });

  const households = useQueryHouseholds({
    includeBalances: true,
    enabled: wantAccountsAndHouseholds,
  });

  const householdsById = _.keyBy(households.data?.data ?? [], "id");

  switch (key) {
    case "accounts-missing-cash-target":
      return {
        isPending: cashSettings.isPending,
        ids:
          typeof cashSettings.data?.data === "undefined"
            ? undefined
            : cashSettings.data.data
                .filter(noCashTarget)
                .map((account) => account.accountId),
      };
    case "accounts-excess-cash":
      return {
        isPending: opportunities.isPending,
        ids: opportunities.data?.data?.accountsWithExcessCash,
      };
    case "accounts-cash-activity":
      return {
        isPending: cashActivity.isPending,
        ids: cashActivity.data?.data?.accountsWithCashActivity,
      };
    case "accounts-new":
      return { isPending: newAccounts.isPending, ids: newAccounts.data };
    case "accounts-no-fee-structure":
      return {
        isPending: accounts.isPending || households.isPending,
        ids:
          typeof accounts.data?.data === "undefined"
            ? undefined
            : accountsWithoutFeeStructure(
                accounts.data.data,
                householdsById,
              ).map((account) => account.id),
      };
    case "accounts-no-model":
      return {
        isPending: accounts.isPending || households.isPending,
        ids:
          typeof accounts.data?.data === "undefined"
            ? undefined
            : accountsWithoutModel(accounts.data.data, householdsById).map(
                (account) => account.id,
              ),
      };
    default:
      return { isPending: false, ids: undefined };
  }
}

/**
 * Re-derive the household ids backing a household-entity insight key. The
 * households are already loaded client-side, so no extra backend call.
 */
export function useInsightHouseholdIds(key: InsightKey | null): {
  isPending: boolean;
  ids: number[] | undefined;
} {
  const isHousehold =
    key === "households-no-fee-structure" || key === "households-no-model";

  const households = useQueryHouseholds({
    includeBalances: true,
    enabled: isHousehold,
  });

  switch (key) {
    case "households-no-fee-structure":
      return {
        isPending: households.isPending,
        ids:
          typeof households.data?.data === "undefined"
            ? undefined
            : householdsWithoutFeeStructure(households.data.data).map(
                (household) => household.id,
              ),
      };
    case "households-no-model":
      return {
        isPending: households.isPending,
        ids:
          typeof households.data?.data === "undefined"
            ? undefined
            : householdsWithoutModel(households.data.data).map(
                (household) => household.id,
              ),
      };
    default:
      return { isPending: false, ids: undefined };
  }
}
